import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import api from "@/lib/api";
import { Eye, Share2, Users, TrendingUp, Target, BarChart3, PlayCircle, MousePointerClick } from "lucide-react";

const RANGES = [[30, "30 Days"], [90, "90 Days"], [365, "1 Year"]];

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/analytics?days=${days}`).then(({ data }) => setD(data)).catch(() => setD(null)).finally(() => setLoading(false));
  }, [days]);

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto" data-testid="admin-analytics-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="label-overline text-[#E63946] flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Recruiting Intelligence</div>
            <h1 className="font-display text-3xl uppercase tracking-tight mt-1">Recruiting Analytics</h1>
            <p className="text-sm text-slate-500 mt-1">Operational engagement trends across the platform. Aggregate signals — no player is shown who viewed them.</p>
          </div>
          <div className="flex gap-1">
            {RANGES.map(([v, l]) => (
              <button key={v} data-testid={`analytics-range-${v}`} onClick={() => setDays(v)}
                className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm ${days === v ? "bg-[#0A192F] text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
            ))}
          </div>
        </div>

        {loading || !d ? (
          <div className="py-20 text-center label-overline text-slate-400">{loading ? "Loading analytics…" : "No analytics available."}</div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6" data-testid="analytics-totals">
              <Total label="Profile Views" value={d.totals.profile_views} icon={Eye} />
              <Total label="Portfolio Opens" value={d.totals.portfolio_opens} icon={BarChart3} />
              <Total label="Shares" value={d.totals.shares} icon={Share2} />
              <Total label="Link Clicks" value={d.totals.link_clicks} icon={MousePointerClick} />
              <Total label="Video Views" value={d.totals.video_views} icon={PlayCircle} />
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mt-6">
              <PlayerList title="Most Viewed Players" icon={Eye} rows={d.most_viewed_players} unit="views" testid="most-viewed" />
              <PlayerList title="Most Shared Portfolios" icon={Share2} rows={d.most_shared_portfolios} unit="shares" testid="most-shared" />
              <PlayerList title="Most Followed Players" icon={Users} rows={d.most_followed_players} unit="recruiters" testid="most-followed" />
              <PlayerList title="Fastest Growing Interest" icon={TrendingUp} rows={d.fastest_growing} unit="growth" testid="fastest-growing" growth />
              <PlayerList title="Most Viewed Videos" icon={PlayCircle} rows={d.most_viewed_videos} unit="views" testid="most-viewed-videos" />

              <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="link-click-breakdown">
                <div className="label-overline flex items-center gap-2 mb-3"><MousePointerClick className="w-4 h-4 text-[#E63946]" /> Outbound Link Clicks</div>
                {(!d.link_click_breakdown || d.link_click_breakdown.length === 0) ? <Empty /> : (
                  <ul className="space-y-2">
                    {d.link_click_breakdown.map((r) => (
                      <li key={r.target} className="flex items-center justify-between text-sm">
                        <span className="font-semibold capitalize">{String(r.target).replace(/_/g, " ")}</span>
                        <span className="font-data text-[#0A192F]">{r.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="bg-white border border-slate-200 rounded-sm p-5 lg:col-span-2" data-testid="most-active-recruiters">
                <div className="label-overline flex items-center gap-2 mb-3"><Target className="w-4 h-4 text-[#E63946]" /> Most Active Recruiters</div>
                {d.most_active_recruiters.length === 0 ? <Empty /> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-slate-50 border-y border-slate-200">
                        <th className="text-left p-2 label-overline">Recruiter</th>
                        <th className="text-left p-2 label-overline">Organization</th>
                        <th className="text-left p-2 label-overline">Type</th>
                        <th className="text-right p-2 label-overline">Actions</th>
                      </tr></thead>
                      <tbody>
                        {d.most_active_recruiters.map((r, i) => (
                          <tr key={i} className="border-b border-slate-100 last:border-0">
                            <td className="p-2 font-semibold">{r.name || "—"}</td>
                            <td className="p-2 text-slate-600">{r.organization || "—"}</td>
                            <td className="p-2 text-slate-500 text-xs">{r.recruiter_type || "—"}</td>
                            <td className="p-2 text-right font-data">{r.events}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function Total({ label, value, icon: Icon }) {
  return (
    <div className="bg-[#0A192F] text-white rounded-sm p-5">
      <div className="flex items-center gap-2 label-overline text-white/60"><Icon className="w-4 h-4 text-[#E63946]" /> {label}</div>
      <div className="font-data text-4xl mt-2">{value ?? 0}</div>
    </div>
  );
}

function PlayerList({ title, icon: Icon, rows, unit, testid, growth }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid={testid}>
      <div className="label-overline flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-[#E63946]" /> {title}</div>
      {(!rows || rows.length === 0) ? <Empty /> : (
        <ol className="space-y-2">
          {rows.map((r, i) => (
            <li key={r.id} className="flex items-center gap-3">
              <span className="font-data text-slate-400 w-5 text-right">{i + 1}</span>
              <div className="w-9 h-9 rounded-sm overflow-hidden bg-slate-100 shrink-0 grid place-items-center">
                {r.profile_photo_url ? <img src={r.profile_photo_url} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-slate-400">{r.first_name?.[0]}{r.last_name?.[0]}</span>}
              </div>
              <Link to={`/prospects/${r.slug}`} className="min-w-0 flex-1 hover:text-[#E63946]">
                <div className="text-sm font-semibold truncate">{r.first_name} {r.last_name}</div>
                <div className="text-[11px] text-slate-400">{[r.primary_position, r.graduation_year, r.province].filter(Boolean).join(" · ")}</div>
              </Link>
              <span className="font-data text-lg text-[#0A192F]">
                {growth ? `${r.delta > 0 ? "+" : ""}${r.delta}` : r.count}
                <span className="text-[10px] text-slate-400 ml-1">{unit}</span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function Empty() {
  return <p className="text-xs text-slate-400 py-3">No data in this range yet — engagement will appear as players are viewed and shared.</p>;
}
